import { measure } from '@/components/Analytics/Analytics';
import useIsNative, { useNativePlatform } from '@/hooks/native/useIsNative';
import useNativeMessages, {
	useNativeMessageCallback,
} from '@/hooks/native/useNativeMessages';
import useHaptics from '@/hooks/useHaptics';
import cn from '@/lib/cs';
import {
	Dispatch,
	FC,
	SetStateAction,
	TouchEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

const getTouchesDistance = (event: TouchEvent) => {
	if (event.touches.length < 2) return 0;
	const distanceInPixels = Math.hypot(
		event.touches[0].pageX - event.touches[1].pageX,
		event.touches[0].pageY - event.touches[1].pageY,
	);
	const distancePercentageRelativeToScreen =
		distanceInPixels / Math.hypot(window.innerWidth, window.innerHeight);
	return distancePercentageRelativeToScreen;
};

const ZoomAndFocusControls: FC<{
	updateZoom: (zoom: number) => void;
}> = ({ updateZoom }) => {
	const isNative = useIsNative();
	const sliderRef = useRef<HTMLInputElement>(null);

	const [isZooming, setIsZooming] = useState(false);
	const [zoomFactor, setZoomFactor] = useState(0);

	// Platform specific zoom intensity
	const nativePlatform = useNativePlatform();
	const zoomButtonIntensity = useMemo(
		() => (nativePlatform === 'ios' ? 0.2 : 1),
		[nativePlatform],
	);
	const zoomPinchIntensity = useMemo(
		() => (nativePlatform === 'ios' ? 100 : 50),
		[nativePlatform],
	);

	// Analytics measurement
	const hasMeasuredZoom = useRef(false);
	const measureZoom = () => {
		if (!hasMeasuredZoom.current) {
			measure('camera-zoom');
			hasMeasuredZoom.current = true;
		}
	};

	// Pinch to zoom starting information
	const pinchToZoomStart = useRef<{
		x: number;
		y: number;
		distance: number;
	} | null>(null);

	// Pinch to zoom start gesture
	const onTouchStart = useCallback((event: TouchEvent) => {
		if (event.touches.length === 2) {
			setIsZooming(true);
			measureZoom();
			pinchToZoomStart.current = {
				x: event.touches[0].pageX,
				y: event.touches[0].pageY,
				distance: getTouchesDistance(event),
			};
		}
	}, []);

	// Pinch to zoom move gesture
	const onTouchMove = useCallback(
		(event: TouchEvent) => {
			if (pinchToZoomStart.current) {
				// if (event.cancelable) event.preventDefault();
				const distance = getTouchesDistance(event);
				const zoomBy =
					(distance - pinchToZoomStart.current.distance) /
					zoomPinchIntensity;
				setZoomFactor((prev) =>
					Math.min(Math.max(prev + zoomBy, 0), 1),
				);
			}
		},
		[zoomPinchIntensity, setZoomFactor],
	);

	// Run updateZoom when zoomFactor changes
	useEffect(() => {
		updateZoom(zoomFactor);
	}, [zoomFactor, updateZoom]);

	// Update focus point on tap
	const [focusPoint, setFocusPoint] = useState<{
		xFactor: number;
		yFactor: number;
	} | null>(null);
	const onNativeMessage = useNativeMessageCallback((key, value) => {
		switch (key) {
			case 'camera.setZoom':
				if (!isZooming) setZoomFactor(Number(value));
				break;

			case 'camera.isFocusSuccessful':
				if (value === 'false') setFocusPoint(null);
				break;

			default:
				break;
		}
	}, []);
	const { sendNativeMessage } = useNativeMessages(onNativeMessage, [
		'camera.isFocusSuccessful',
		'camera.setZoom',
	]);
	const setFocus = useCallback(
		(xFactor: number, yFactor: number) => {
			setFocusPoint({ xFactor, yFactor });
			if (isNative)
				sendNativeMessage('camera.setFocus', `${xFactor},${yFactor}`);
		},
		[isNative, sendNativeMessage],
	);

	return (
		<>
			<div
				className="group absolute inset-0 size-full overflow-hidden"
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={() => {
					if (isNative && isZooming) {
						setIsZooming(false);
						setFocusPoint({ xFactor: 0.5, yFactor: 0.5 });
						sendNativeMessage('camera.setFocus', '0.5,0.5');
					}
				}}
				onPointerDown={(event) => {
					if (isZooming) return;
					measure('camera-focus');
					const rect = event.currentTarget.getBoundingClientRect();
					const xFactor = (event.clientX - rect.left) / rect.width;
					const yFactor = (event.clientY - rect.top) / rect.height;
					setFocus(xFactor, yFactor);
				}}>
				<div
					className={cn(
						'absolute size-16 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all ease-out shadow-lg duration-1000',
						focusPoint
							? 'scale-100 group-active:scale-50 group-active:opacity-0 group-active:duration-100 group-active:-rotate-180 opacity-100'
							: 'scale-0 opacity-0 rotate-180',
					)}
					style={{
						left: focusPoint
							? `calc(${focusPoint.xFactor * 100}% - 12px)`
							: '50%',
						top: focusPoint
							? `calc(${focusPoint.yFactor * 100}% - 12px)`
							: '50%',
					}}>
					<div className="absolute size-full scale-110 rounded-full border-8 border-primary opacity-20 shadow-md" />
					<div className="absolute size-full rounded-full border-4 border-dotted border-white" />
				</div>
			</div>
			<ol
				aria-label="Zoomen"
				className="absolute end-6 top-1/2 z-10 flex -translate-y-1/2 flex-col rounded-full bg-gray-950/60 backdrop-blur-sm md:end-8 lg:end-10">
				<ZoomButton
					zoomBy={0.1 * zoomButtonIntensity}
					setZoomFactor={(zoom) => {
						measureZoom();
						setZoomFactor(zoom);
					}}
				/>
				<div className="flex h-32 w-10 items-center justify-center">
					<input
						ref={sliderRef}
						type="range"
						min="0"
						max="1"
						step="0.05"
						aria-label="Zoomen"
						value={zoomFactor}
						className="block w-32 translate-x-1 rotate-90 -scale-x-100 accent-white"
						onChange={(event) => {
							measureZoom();
							setZoomFactor(Number(event.target.value));
						}}
					/>
				</div>
				<ZoomButton
					zoomBy={-0.1 * zoomButtonIntensity}
					setZoomFactor={(zoom) => {
						measureZoom();
						setZoomFactor(zoom);
					}}
				/>
			</ol>
		</>
	);
};

const ZoomButton = ({
	zoomBy,
	setZoomFactor,
}: {
	zoomBy: number;
	setZoomFactor: Dispatch<SetStateAction<number>>;
}) => {
	const { withOnButtonPress } = useHaptics();

	return (
		<li>
			<button
				aria-label={`Zoom ${zoomBy < 0 ? 'uit' : 'in'}`}
				onClick={() =>
					setZoomFactor((prev) =>
						Math.min(Math.max(prev + zoomBy, 0), 1),
					)
				}
				className="size-10 rounded-full text-2xl xxs:size-12"
				{...withOnButtonPress}>
				{zoomBy < 0 ? '-' : '+'}
			</button>
		</li>
	);
};

export default ZoomAndFocusControls;
